import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { buildDataDomainUrl } from "../../../utils/dataDomainUrl";
import { Search, Grid, List, ChevronDown, ListFilter } from "lucide-react";
import { SubdomainCard } from "./SubdomainCard";
import ModelSummary from "./ModelSummary";
import { CardMetric } from "../ui/CardMetric";
import ERLandingPage from "../ERDiagram/pages/ERLandingPage";
import ReusableFilterDrawer from "../../common/ReusableFilterDrawer";
import AppliedFilters from "../../common/AppliedFilters";

const DATA_LAYER_OPTIONS = [
  { value: "silver", label: "Silver" },
  { value: "gold", label: "Gold" },
  { value: "ingestion", label: "Ingestion" },
  { value: "bronze", label: "Bronze" },
  { value: "analyticalProduct", label: "Analytical Product" },
];

export const DomainOverview = ({
  domain,
  onSubdomainClick,
  onFilterChange,
  appliedFilters,
  navigation,
  activeDomainTypeTab = "all",
  onDomainTypeTabChange,
  regions = [],
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;
  const [domainsOpen, setDomainsOpen] = useState(false);
  const [sourceSystemsOpen, setSourceSystemsOpen] = useState(false);
  const [analyticalOpen, setAnalyticalOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const filteredSubdomains = useMemo(() => {
    if (!searchTerm.trim()) {
      return domain.domains_L2;
    }

    return domain.domains_L2.filter(
      (subdomain) =>
        subdomain.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        subdomain.description.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [domain.domains_L2, searchTerm]);

  const analyticalDomains = useMemo(
    () => filteredSubdomains.filter((s) => s.name.toLowerCase().includes("analytical")),
    [filteredSubdomains]
  );
  const sourceSystemDomains = useMemo(
    () => filteredSubdomains.filter((s) => s.name.toLowerCase().includes("source system")),
    [filteredSubdomains]
  );
  const regularDomains = useMemo(
    () => filteredSubdomains.filter(
      (s) => !s.name.toLowerCase().includes("analytical") && !s.name.toLowerCase().includes("source system")
    ),
    [filteredSubdomains]
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredSubdomains.length / itemsPerPage),
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const totalAssets = domain.domains_L2.reduce(
    (sum, sub) => sum + parseInt(sub.assets || 0),
    0,
  );
  const totalProducts = domain.domains_L2.reduce(
    (sum, sub) => sum + parseInt(sub.product_count || 0),
    0,
  );
  const averageScore = domain?.score ? domain?.score : "0%";

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const getSubdomainUrl = (subdomainName) => {
    return buildDataDomainUrl(
      {
        selectedL1: navigation?.selectedL1,
        selectedL2: subdomainName,
      },
      { dataLayers: appliedFilters?.dataLayer || [] },
    );
  };

  const getDomainTypeTabUrl = (tabId) => {
    const selectedL1 = navigation?.selectedL1 || domain?.parent_L1;
    const tabLayers =
      tabId === "domains"
        ? ["silver", "gold"]
        : tabId === "sourceSystems"
          ? ["ingestion", "bronze"]
          : tabId === "analyticalProduct"
            ? ["analyticalProduct"]
            : [];

    return buildDataDomainUrl(
      { selectedL1 },
      { dataLayers: tabLayers },
    );
  };

  const filterSections = useMemo(() => [
    {
      fields: [
        {
          type: "multiselect",
          key: "regions",
          label: "Region",
          options: regions.map((r) => ({ value: r, label: r })),
        },
        {
          type: "multiselect",
          key: "dataLayer",
          label: "Data Layer",
          options: DATA_LAYER_OPTIONS,
        },
      ],
    },
  ], [regions]);

  const activeFilterCount = (appliedFilters?.regions?.length || 0) + (appliedFilters?.dataLayer?.length || 0);

  const appliedChips = [
    ...(appliedFilters?.regions || []).map((r) => ({ label: "Region", value: r })),
    ...(appliedFilters?.dataLayer || []).map((l) => ({
      label: "Data Layer",
      value: DATA_LAYER_OPTIONS.find((o) => o.value === l)?.label || l,
    })),
  ];

  const renderContent = (subdomains) => {
    if (viewMode === "grid") {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-[4px] pb-[4px] pl-2 pr-2">
          {subdomains.map((subdomain) => (
            <SubdomainCard
              key={subdomain.name}
              subdomain={subdomain}
              linkTo={getSubdomainUrl(subdomain.name)}
              onClick={() => onSubdomainClick(subdomain.name)}
            />
          ))}
        </div>
      );
    }
    return (
      <div className="bg-[#eee] rounded-lg border border-gray-200 overflow-hidden shadow-sm mr-[20px] ml-[20px]">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quality Score</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assets</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Products</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {subdomains.map((subdomain) => {
              const isRowEmpty = parseInt(subdomain.assets || 0) === 0 && parseInt(subdomain.product_count || 0) === 0;
              return (
                <tr
                  key={`${subdomain.name}-table`}
                  className={`transition-colors duration-150 ${isRowEmpty ? "cursor-default" : "cursor-pointer hover:bg-gray-50"}`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link
                      to={isRowEmpty ? "#" : getSubdomainUrl(subdomain.name)}
                      onClick={(e) => {
                        if (isRowEmpty) { e.preventDefault(); return; }
                        const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
                        if (isModifiedClick) return;
                        e.preventDefault();
                        onSubdomainClick(subdomain.name);
                      }}
                      className="block"
                    >
                      <div className="text-sm font-medium text-gray-900">{subdomain.name}</div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">{subdomain.description}</div>
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      parseFloat(subdomain.score.replace("%", "")) >= 90
                        ? "bg-green-100 text-green-800"
                        : parseFloat(subdomain.score.replace("%", "")) >= 80
                          ? "bg-blue-100 text-blue-800"
                          : "bg-orange-100 text-orange-800"
                    }`}>
                      {subdomain.score}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {parseInt(subdomain.assets || 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {parseInt(subdomain.product_count || 0).toLocaleString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSection = (title, subdomains, open, onToggle) => {
    if (subdomains.length === 0) return null;
    return (
      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <button
          className="flex items-center gap-2 w-full text-left py-3 px-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={onToggle}
        >
          <ChevronDown
            className={`w-[20px] h-[20px] text-gray-500 transition-transform duration-200 ${open ? "" : "-rotate-90"}`}
          />
          <span className="text-xl font-bold text-gray-900">{title}</span>
          <span className="text-sm text-gray-500 ml-1">({subdomains.length})</span>
        </button>
        {open && (
          <div className="p-3 bg-white">
            {renderContent(subdomains)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-7">
      {/* Domain Header */}
      <div className="rounded-lg py-2 text-white relative p-[20px]">
        <div
          style={{ alignContent: "center" }}
          className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-3"
        >
          {/* <FileAxis3DIcon className="text-[#FF03F3]" /> */}
          <div className="flex items-center gap-2">
            <h1 className="text-[15px] font-bold text-[#bb1ccc]">
              {domain.parent_L1} (Line of Business)
            </h1>
          </div>
         
        </div>

        <p className="text-white-500 text-lg leading-relaxed text-left text-black ">
          {domain.parent_L1_description}
        </p>
      </div>
        <div className="flex items-center space-x-3 mt-4 ml-4">
            <div className="flex-shrink-0">
              <ERLandingPage navigation={navigation} />
            </div>
            <div className="flex-shrink-0">
              <ModelSummary
                title={domain.parent_L1}
                text={domain.parent_L1_description_expanded || "N/A"}
              />
            </div>
          </div>

      {/* Summary Metrics */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">

    <CardMetric
      title="Total Products"
      value={totalProducts.toLocaleString()}
      variant="success"
    />

    <CardMetric
      title="Quality Score"
      value={averageScore}
      variant="warning"
    />

    <CardMetric
      title="Total Assets"
      value={totalAssets.toLocaleString()}
      variant="primary"
    />

  </div>
</div>

      {/* Controls and Filters */}
      <div className="space-y-4 pb-[20px] pl-[20px] pr-[20px]">
        {/* First Row: Search, Filters Button and View Mode */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0 gap-3">
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search
                style={{ width: "15px" }}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search for domains..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ paddingLeft: "35px" }}
                className="w-full pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[0000a0] focus:border-transparent transition-colors h-[45px]"
              />
            </div>
            <button
              onClick={() => setFilterDrawerOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition-colors h-[45px] whitespace-nowrap bg-[#bb1ccc] text-white border-[#bb1ccc] hover:bg-[#4f52d1]"
            >
              <ListFilter className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="bg-white text-[#bb1ccc] text-xs rounded-full px-1.5 py-0.5 leading-none font-bold">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode("grid")}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors h-[45px] ${
                viewMode === "grid"
                  ? "bg-[#FF03F3] text-white border-[#FF03F3]"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              <Grid className="w-[18px] h-[18px]" />
              <span>Grid</span>
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors h-[45px] ${
                viewMode === "table"
                  ? "bg-[#FF03F3] text-white border-[#FF03F3]"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              <List className="w-[18px] h-[18px]" />
              <span>Table</span>
            </button>
          </div>
        </div>
      </div>


      <div className="pl-[20px] pr-[20px]">
        <AppliedFilters chips={appliedChips} />
      </div>

      {/* Results Info */}
      <div className="flex items-center justify-between pl-[20px] pr-[20px] pt-[5px] pb-[5px]">
        <div className="text-sm text-gray-600">
          {searchTerm && (
            <span className="mr-4">
              {filteredSubdomains.length} result
              {filteredSubdomains.length !== 1 ? "s" : ""} for &quot;{searchTerm}&quot;
            </span>
          )}
          <span>
            {filteredSubdomains.length} of {domain.domains_L2.length} domains
          </span>
        </div>
      </div>

      {/* Content */}
      {filteredSubdomains.length === 0 ? (
        <div className="text-center py-16">
          <Search className="w-[64px] h-[64px] text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">
            {searchTerm ? "No domains found" : "No domains available"}
          </h3>
          <p className="text-gray-500 max-w-md mx-auto">
            {searchTerm
              ? `No domains match "${searchTerm}". Try adjusting your search terms.`
              : "There are no domains to display at this time."}
          </p>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
            >
              Clear search
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4 pl-[20px] pr-[20px] pb-6">
          {renderSection("Domains", regularDomains, domainsOpen, () => setDomainsOpen(!domainsOpen))}
          {renderSection("Source Systems", sourceSystemDomains, sourceSystemsOpen, () => setSourceSystemsOpen(!sourceSystemsOpen))}
          {renderSection("Analytical", analyticalDomains, analyticalOpen, () => setAnalyticalOpen(!analyticalOpen))}
        </div>
      )}

      <ReusableFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        sections={filterSections}
        values={{ regions: appliedFilters?.regions || [], dataLayer: appliedFilters?.dataLayer || [] }}
        onApply={(filters) => onFilterChange(filters.regions || [], filters.dataLayer || [])}
        onReset={() => onFilterChange([], [])}
        flat
        subtitle="Filter by region & data layer"
      />
    </div>
  );
};
